import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Star,
  MessageSquare,
  Loader2,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Store,
  Download,
  Sparkles,
  Clock,
  PlayCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stores below this rating are flagged for attention
const LOW_RATING_THRESHOLD = 4.0;

const StoresDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exportingReviews, setExportingReviews] = useState(false);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [runningNow, setRunningNow] = useState(false);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/schedule/status`);
      setSchedule(response.data);
    } catch (error) {
      console.error("Error fetching schedule status:", error);
    }
  };

  const handleRunNow = async () => {
    const confirmed = window.confirm(
      "Ini akan memicu ulang scraping SEMUA toko yang sudah pernah di-scrape (bisa 30-60+ menit berjalan di background). Lanjutkan?"
    );
    if (!confirmed) return;

    setRunningNow(true);
    try {
      const response = await axios.post(`${API}/schedule/run-now`);
      toast.success(`${response.data.queued} toko dijadwalkan untuk di-scrape ulang`);
      fetchSchedule();
    } catch (error) {
      console.error("Error running schedule now:", error);
      toast.error(error.response?.data?.detail || "Gagal memicu scraping");
    } finally {
      setRunningNow(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    return new Intl.DateTimeFormat("id-ID", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString));
  };

  const fetchSummary = async (showToast = false) => {
    if (showToast) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/stores-summary`);
      setSummary(response.data);
      if (showToast) toast.success("Dashboard diperbarui");
    } catch (error) {
      console.error("Error fetching stores summary:", error);
      toast.error("Gagal memuat ringkasan toko");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleAnalyzeAll = async () => {
    setAnalyzingAll(true);
    try {
      const response = await axios.post(`${API}/stores-summary/analyze-all`);
      const { queued } = response.data;
      if (queued === 0) {
        toast.info("Semua toko sudah punya analisa AI. Refresh untuk lihat, atau analisa ulang dari halaman detail toko.");
      } else {
        toast.success(`Analisa AI untuk ${queued} toko dimulai di background. Refresh beberapa saat lagi.`);
      }
    } catch (error) {
      console.error("Error triggering bulk analysis:", error);
      toast.error(error.response?.data?.detail || "Gagal memulai analisa AI");
    } finally {
      setAnalyzingAll(false);
    }
  };

  const downloadCsv = (filename, headers, rows) => {
    const csvRows = [
      headers.join(","),
      ...rows,
    ];
    const csvContent = "\uFEFF" + csvRows.join("\n"); // BOM for UTF-8 Excel compatibility
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""').replace(/\n/g, " ")}"`;

  const handleExportSummaryCsv = () => {
    const stores = summary?.stores || [];
    if (stores.length === 0) {
      toast.error("Belum ada data toko untuk diekspor");
      return;
    }
    const headers = ["Kode Toko", "Nama Tempat", "Rating", "Total Review", "Status", "Terakhir Discrape"];
    const rows = stores.map((s) =>
      [
        csvEscape(s.store_code),
        csvEscape(s.place_name),
        s.place_rating ?? "",
        s.total_reviews_scraped ?? 0,
        csvEscape(s.status),
        csvEscape(s.completed_at || s.created_at),
      ].join(",")
    );
    downloadCsv(`ringkasan_33_cabang_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
    toast.success(`Ringkasan ${stores.length} toko berhasil diunduh`);
  };

  const handleExportAllReviewsCsv = async () => {
    setExportingReviews(true);
    try {
      toast.info("Menyiapkan CSV gabungan, mohon tunggu (bisa memakan waktu jika review banyak)...");
      const response = await axios.get(`${API}/stores-summary/reviews-export`);
      const { reviews, total_reviews, total_stores } = response.data;

      if (!reviews || reviews.length === 0) {
        toast.error("Belum ada review yang bisa diekspor");
        return;
      }

      const headers = ["Kode Toko", "Nama Tempat", "Author", "Rating", "Review Text", "Tanggal", "Likes"];
      const rows = reviews.map((r) =>
        [
          csvEscape(r.store_code),
          csvEscape(r.place_name),
          csvEscape(r.author_name),
          r.rating ?? "",
          csvEscape(r.review_text),
          csvEscape(r.review_date),
          r.likes_count ?? 0,
        ].join(",")
      );
      downloadCsv(
        `semua_review_33_cabang_${new Date().toISOString().slice(0, 10)}.csv`,
        headers,
        rows
      );
      toast.success(`${total_reviews} review dari ${total_stores} toko berhasil diunduh`);
    } catch (error) {
      console.error("Error exporting all reviews:", error);
      toast.error("Gagal mengekspor gabungan review");
    } finally {
      setExportingReviews(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchSchedule();
    const interval = setInterval(() => fetchSummary(), 10000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: "Menunggu", variant: "secondary" },
      processing: { label: "Memproses", variant: "default" },
      completed: { label: "Selesai", variant: "success" },
      failed: { label: "Gagal", variant: "destructive" },
    };
    const c = config[status] || config.pending;
    return <Badge variant={c.variant}>{c.label}</Badge>;
  };

  const stores = summary?.stores || [];
  const lowRatedStores = stores.filter(
    (s) => s.place_rating != null && s.place_rating < LOW_RATING_THRESHOLD
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Kembali
            </Button>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Store className="w-6 h-6 text-blue-600" />
              Dashboard Review 33 Cabang
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={handleAnalyzeAll}
              disabled={analyzingAll}
              data-testid="analyze-all-btn"
              className="bg-purple-600 hover:bg-purple-700"
            >
              {analyzingAll ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-1" />
              )}
              Analisa Semua Toko (AI)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportSummaryCsv}
              data-testid="export-summary-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Export Ringkasan (CSV)
            </Button>
            <Button
              size="sm"
              onClick={handleExportAllReviewsCsv}
              disabled={exportingReviews}
              data-testid="export-all-reviews-csv-btn"
              className="bg-green-600 hover:bg-green-700"
            >
              {exportingReviews ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1" />
              )}
              Download Semua Review (CSV)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchSummary(true)}
              disabled={refreshing}
              data-testid="refresh-summary-btn"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Auto-scrape schedule status */}
        {schedule && (
          <Card
            className={schedule.enabled ? "border-green-200 bg-green-50/40" : "border-gray-200"}
            data-testid="schedule-status-card"
          >
            <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Clock className={`w-5 h-5 ${schedule.enabled ? "text-green-600" : "text-gray-400"}`} />
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    Auto-scrape: {schedule.enabled ? "Aktif" : "Nonaktif"}
                    {schedule.enabled && (
                      <span className="text-gray-500 font-normal">
                        {" "}&middot; jam {schedule.scheduled_times?.join(", ")} {schedule.timezone}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {schedule.last_run?.started_at
                      ? `Terakhir jalan: ${formatDateTime(schedule.last_run.started_at)} (${schedule.last_run.stores_queued} toko)`
                      : "Belum pernah dijalankan"}
                    {schedule.next_runs?.length > 0 && (
                      <> &middot; Berikutnya: {formatDateTime(schedule.next_runs[0].next_run_time)}</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRunNow}
                disabled={runningNow}
                data-testid="run-schedule-now-btn"
              >
                {runningNow ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <PlayCircle className="w-4 h-4 mr-1" />
                )}
                Jalankan Sekarang
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Top stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Toko Terpantau</p>
                <p className="text-3xl font-bold text-blue-900">{summary?.total_stores ?? 0} / 33</p>
              </div>
              <Store className="w-8 h-8 text-blue-600" />
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Rata-rata Rating</p>
                <p className="text-3xl font-bold text-purple-900">
                  {summary?.avg_rating != null ? summary.avg_rating.toFixed(2) : "-"}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700 font-medium">
                  Perlu Perhatian (&lt; {LOW_RATING_THRESHOLD})
                </p>
                <p className="text-3xl font-bold text-amber-900">{lowRatedStores.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </CardContent>
          </Card>
        </div>

        {/* Cross-branch issue aggregation (from AI analysis) */}
        {summary?.cross_branch_issues?.length > 0 && (
          <Card data-testid="cross-branch-issues-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Ringkasan Masalah Lintas Cabang
              </CardTitle>
              <CardDescription>
                Digabung dari analisa AI {summary.stores_analyzed} toko yang sudah dianalisa. Kategori
                paling sering muncul di paling atas.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {summary.cross_branch_issues.map((issue) => (
                  <div
                    key={issue.name}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100"
                  >
                    <span className="text-sm font-medium text-gray-800">{issue.name}</span>
                    <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
                      <span>{issue.store_count} toko terdampak</span>
                      <Badge variant="outline">{issue.total_mentions} review</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {stores.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-2">
              <Store className="w-16 h-16 mx-auto text-gray-300" />
              <p className="text-gray-500 text-lg">Belum ada toko yang di-scrape dengan kode toko</p>
              <p className="text-gray-400 text-sm">
                Kembali ke halaman utama, gunakan mode "Bulk (Banyak Toko)" dan isi kode toko (M01-M33) untuk tiap URL.
              </p>
              <Button className="mt-2" onClick={() => navigate("/")}>
                Mulai Scraping
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Ringkasan per Cabang</CardTitle>
              <CardDescription>
                Menampilkan hasil scraping terakhir untuk tiap kode toko. Klik baris untuk lihat detail review.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="stores-summary-table">
                  <thead className="bg-gray-50 text-gray-600 text-left">
                    <tr>
                      <th className="px-4 py-3 font-medium">Kode</th>
                      <th className="px-4 py-3 font-medium">Nama Tempat (Google Maps)</th>
                      <th className="px-4 py-3 font-medium">Rating</th>
                      <th className="px-4 py-3 font-medium">Total Reviews</th>
                      <th className="px-4 py-3 font-medium">Top Masalah (AI)</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Terakhir Discrape</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {stores.map((store) => {
                      const isLow = store.place_rating != null && store.place_rating < LOW_RATING_THRESHOLD;
                      return (
                        <tr
                          key={store.id}
                          className={`cursor-pointer hover:bg-blue-50 transition-colors ${isLow ? "bg-amber-50" : ""}`}
                          onClick={() => navigate(`/job/${store.id}`)}
                          data-testid={`store-row-${store.store_code}`}
                        >
                          <td className="px-4 py-3 font-mono font-semibold">{store.store_code}</td>
                          <td className="px-4 py-3 max-w-xs truncate">{store.place_name || "-"}</td>
                          <td className="px-4 py-3">
                            {store.place_rating != null ? (
                              <span className={`inline-flex items-center gap-1 font-semibold ${isLow ? "text-amber-700" : "text-gray-900"}`}>
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                {store.place_rating}
                                {isLow && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 ml-1" />}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1">
                              <MessageSquare className="w-3.5 h-3.5 text-purple-600" />
                              {store.total_reviews_scraped ?? 0}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {store.top_issue ? (
                              <Badge variant="outline" className="text-xs whitespace-nowrap">
                                {store.top_issue}
                              </Badge>
                            ) : (
                              <span className="text-xs text-gray-400">Belum dianalisa</span>
                            )}
                          </td>
                          <td className="px-4 py-3">{getStatusBadge(store.status)}</td>
                          <td className="px-4 py-3 text-gray-500">
                            {formatDate(store.completed_at || store.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StoresDashboard;
